import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Home,
  Calendar,
  Clipboard,
  MoreHorizontal,
  Users,
  Euro,
  FileArchive,
  Package,
  Lightbulb,
  BarChart,
  LifeBuoy,
  Settings,
  Megaphone,
  Handshake,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityScope } from '@/contexts/CommunityScopeContext';
import { hasAccessByRole, userRoleToSystemRoles } from '@/utils/roleUtils';
import { normalizeCommunitySectionSelection } from '@/data/communitySections';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';

export const BottomNavigation: React.FC = () => {
  const { currentUser } = useAuth();
  const { activeCommunity, activeMembershipProfile, isGlobalAdmin } = useCommunityScope();
  const navigate = useNavigate();

  const effectiveRole = React.useMemo(() => {
    if (isGlobalAdmin) {
      return currentUser?.role ?? null;
    }
    return activeMembershipProfile?.role ?? null;
  }, [activeMembershipProfile?.role, currentUser?.role, isGlobalAdmin]);

  const paymentsAllowedRoles = ['manager', 'president', 'vocal', 'neighbor'];
  const isRenter = effectiveRole === 'renter';
  const isAdmin = effectiveRole === 'admin';
  const canAccessPaymentsPage = effectiveRole ? paymentsAllowedRoles.includes(effectiveRole) : false;
  const hasAdminAccess = effectiveRole ? hasAccessByRole(effectiveRole, 'admin') : false;
  const supportAllowedRoles = ['admin', 'admin_general', 'president', 'vocal', 'owner'];
  
  const userRoles = effectiveRole
    ? Array.from(new Set([effectiveRole, ...(userRoleToSystemRoles[effectiveRole] ?? [])])).filter((role): role is string => Boolean(role))
    : [];
  const canViewSupport = userRoles.some(role => supportAllowedRoles.includes(role));

  const configuredSections = React.useMemo(
    () => normalizeCommunitySectionSelection(activeCommunity?.availableSections),
    [activeCommunity?.availableSections]
  );
  
  const hasSectionConfig = configuredSections.length > 0;

  const isSectionEnabled = React.useCallback(
    (route: string) => {
      if (route === '/app' || route === '/app/support') {
        return true;
      }
      if (route.startsWith('/app/admin/') || route === '/app/settings') {
        return hasAdminAccess;
      }
      if (route === '/app/payments' && !canAccessPaymentsPage) {
        return false;
      }
      if (isAdmin) {
        return true;
      }
      if (!hasSectionConfig) {
        return true;
      }
      return configuredSections.includes(route);
    },
    [canAccessPaymentsPage, configuredSections, hasAdminAccess, hasSectionConfig, isAdmin]
  );

  const [isOpen, setIsOpen] = React.useState(false);

  // Cierre inmediato del panel y navegación instantánea al pulsar cualquier opción
  const handleNavigate = React.useCallback(
    (to: string) => {
      setIsOpen(false);
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      navigate(to);
    },
    [navigate]
  );

  // Menú secundario para el Drawer "Más"
  const secondaryMenu = React.useMemo(() => {
    const items = [];

    if (!isRenter && isSectionEnabled('/app/communities')) {
      items.push({ to: '/app/communities', label: 'Comunidad', icon: Users });
    }
    if (isSectionEnabled('/app/calendar')) {
      items.push({ to: '/app/calendar', label: 'Calendario', icon: Calendar });
    }
    if (isSectionEnabled('/app/meetings')) {
      items.push({ to: '/app/meetings', label: 'Juntas', icon: Handshake });
    }
    if (canAccessPaymentsPage && isSectionEnabled('/app/payments')) {
      items.push({ to: '/app/payments', label: 'Pagos', icon: Euro });
    }
    if (isSectionEnabled('/app/documents')) {
      items.push({ to: '/app/documents', label: 'Documentos', icon: FileArchive });
    }
    if (isSectionEnabled('/app/providers')) {
      items.push({ to: '/app/providers', label: 'Proveedores', icon: Package });
    }
    if (isSectionEnabled('/app/suggestions')) {
      items.push({ to: '/app/suggestions', label: 'Sugerencias', icon: Lightbulb });
    }
    if (isSectionEnabled('/app/announcements')) {
      items.push({ to: '/app/announcements', label: 'Anuncios', icon: Megaphone });
    }
    if (isSectionEnabled('/app/quick-polls')) {
      items.push({ to: '/app/quick-polls', label: 'Encuestas', icon: BarChart });
    }
    if (hasAdminAccess) {
      items.push({ to: '/app/settings', label: 'Ajustes', icon: Settings });
    }
    if (canViewSupport && isSectionEnabled('/app/support')) {
      items.push({ to: '/app/support', label: 'Soporte', icon: LifeBuoy });
    }

    return items;
  }, [isRenter, isSectionEnabled, canAccessPaymentsPage, hasAdminAccess, canViewSupport]);

  if (!currentUser) return null;

  // Render para rol inquilino (Renter)
  if (isRenter) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur-md border-t border-border/80 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] shadow-2xl flex items-center justify-around px-2">
        {isSectionEnabled('/app/reservations') && (
          <NavLink
            to="/app/reservations"
            className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
          >
            {({ isActive }) => (
              <>
                <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                  <Calendar className="h-[22px] w-[22px]" />
                </div>
                <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Reservas</span>
              </>
            )}
          </NavLink>
        )}

        {isSectionEnabled('/app/calendar') && (
          <NavLink
            to="/app/calendar"
            className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
          >
            {({ isActive }) => (
              <>
                <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                  <Calendar className="h-[22px] w-[22px]" />
                </div>
                <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Calendario</span>
              </>
            )}
          </NavLink>
        )}

        {isSectionEnabled('/app/announcements') && (
          <NavLink
            to="/app/announcements"
            className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
          >
            {({ isActive }) => (
              <>
                <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                  <Megaphone className="h-[22px] w-[22px]" />
                </div>
                <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Anuncios</span>
              </>
            )}
          </NavLink>
        )}

        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <button
              type="button"
              className="flex flex-col items-center justify-center flex-1 py-1 text-muted-foreground transition-all active:scale-95 focus:outline-none focus-visible:ring-0 focus-visible:outline-none select-none"
            >
              <div className="w-12 h-7 flex items-center justify-center rounded-full text-muted-foreground">
                <MoreHorizontal className="h-[22px] w-[22px]" />
              </div>
              <span className="text-[11px] font-medium tracking-tight mt-0.5 leading-none text-muted-foreground">Más</span>
            </button>
          </DrawerTrigger>
          <DrawerContent className="rounded-t-3xl" onCloseAutoFocus={(event) => event.preventDefault()}>
            <DrawerHeader className="pb-2">
              <DrawerTitle className="font-serif text-lg text-center">Menú Principal</DrawerTitle>
              <DrawerDescription className="text-xs text-center">Accede a las gestiones de inquilino</DrawerDescription>
            </DrawerHeader>
            <div className="grid grid-cols-3 gap-3 p-4 pt-1">
              <button
                type="button"
                onClick={() => handleNavigate('/app/profile')}
                className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
              >
                <Settings className="h-6 w-6 text-primary mb-1.5" />
                <span className="text-xs font-medium text-foreground text-center leading-tight">Perfil</span>
              </button>
              {canViewSupport && (
                <button
                  type="button"
                  onClick={() => handleNavigate('/app/support')}
                  className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
                >
                  <LifeBuoy className="h-6 w-6 text-primary mb-1.5" />
                  <span className="text-xs font-medium text-foreground text-center leading-tight">Soporte</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => handleNavigate('/app')}
                aria-label="Ir a Inicio"
                className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
              >
                <img src="/brand-assets/comunihome-logo-v2.png" alt="ComuniHome" className="h-8 w-8 object-contain" />
              </button>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  // Render para Propietarios / Administradores
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur-md border-t border-border/80 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] shadow-2xl flex items-center justify-around px-2">
      {isSectionEnabled('/app') && (
        <NavLink
          to="/app"
          end
          className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
        >
          {({ isActive }) => (
            <>
              <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                <Home className="h-[22px] w-[22px]" />
              </div>
              <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Inicio</span>
            </>
          )}
        </NavLink>
      )}

      {isSectionEnabled('/app/reservations') && (
        <NavLink
          to="/app/reservations"
          className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
        >
          {({ isActive }) => (
            <>
              <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                <Calendar className="h-[22px] w-[22px]" />
              </div>
              <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Reservas</span>
            </>
          )}
        </NavLink>
      )}

      {isSectionEnabled('/app/incidents') && (
        <NavLink
          to="/app/incidents"
          className="flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-95"
        >
          {({ isActive }) => (
            <>
              <div className={cn("w-12 h-7 flex items-center justify-center rounded-full transition-all duration-200", isActive ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground")}>
                <Clipboard className="h-[22px] w-[22px]" />
              </div>
              <span className={cn("text-[11px] font-medium tracking-tight mt-0.5 leading-none", isActive ? "text-primary font-bold" : "text-muted-foreground")}>Incidencias</span>
            </>
          )}
        </NavLink>
      )}

      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>
          <button
            type="button"
            className="flex flex-col items-center justify-center flex-1 py-1 text-muted-foreground transition-all active:scale-95 focus:outline-none focus-visible:ring-0 focus-visible:outline-none select-none"
          >
            <div className="w-12 h-7 flex items-center justify-center rounded-full text-muted-foreground">
              <MoreHorizontal className="h-[22px] w-[22px]" />
            </div>
            <span className="text-[11px] font-medium tracking-tight mt-0.5 leading-none text-muted-foreground">Más</span>
          </button>
        </DrawerTrigger>
        <DrawerContent className="rounded-t-3xl" onCloseAutoFocus={(event) => event.preventDefault()}>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="font-serif text-lg text-center">Menú Principal</DrawerTitle>
            <DrawerDescription className="text-xs text-center">Accede al resto de secciones comunitarias</DrawerDescription>
          </DrawerHeader>
          <div className="grid grid-cols-3 gap-3 p-4 pt-1 max-h-[65vh] overflow-y-auto scrollbars-transparent">
            {secondaryMenu.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.to}
                  onClick={() => handleNavigate(item.to)}
                  className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
                >
                  <Icon className="h-6 w-6 text-primary mb-1.5" />
                  <span className="text-xs font-medium text-foreground text-center leading-tight">{item.label}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handleNavigate('/app/profile')}
              className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
            >
              <Settings className="h-6 w-6 text-primary mb-1.5" />
              <span className="text-xs font-medium text-foreground text-center leading-tight">Perfil</span>
            </button>
            <button
              type="button"
              onClick={() => handleNavigate('/app')}
              aria-label="Ir a Inicio"
              className="flex flex-col items-center justify-center p-3 border border-app-border-soft bg-app-panel text-center active:scale-95 transition-transform rounded-xl"
            >
              <img src="/brand-assets/comunihome-logo-v2.png" alt="ComuniHome" className="h-8 w-8 object-contain" />
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
